import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { isChrome, isDesktop, isIOS, isSafari } from 'react-device-detect';

import Button from '../button';
import ClassworkPrivacyConsent, { Link } from '../classwork-privacy-consent';

import { generateClassroomViewerPayload } from './utils';
import { getPrivacyConsentByUserId, getStatusByAssigmentIdAndStudentIdIn, setPrivacyConsentByUserId } from './utils/lib';
import { useGapi, useGoogleCourseInfo } from '../../lib/hooks';
import { getRedirectToSource, redirectToViewer } from '../../lib/utils';

import { ERROR } from '../../constants/routes';
import { SOURCES } from '../../constants/sources';
import FluidContainer from '../layouts/components/fluid-container';
import ColumnContainer from '../layouts/components/column-container';
import WrongClassroom from '../wrong-classroom';

import ModalContainer from '../../containers/modal';
import { Body, Heading } from '../typography';
import DefaultLayout from '../layouts/deafult-layout';
import DOMPurify from 'dompurify';

const isCrossSiteProblem = isSafari || (isIOS && isChrome);

export const CrossSiteProblemHelpMessage = () => {
    if (isDesktop && isSafari) {
        return (
            <>
                <br></br>
                <b>
                    {"Se hai attivato l'impostazione della privacy non potrai accedere con Google. "}
                    <Link
                        target="_blank"
                        href="https://assistenza.zanichelli.it/support/solutions/articles/4000178183-non-riesco-ad-autenticarmi-con-google-sul-browser-safari-del-mio-dispositivo-desktop-cosa-posso-fare"
                    >
                        {'Leggi come risolvere il problema'}
                    </Link>
                    {' oppure scegli un altro browser.'}
                </b>
            </>
        );
    }

    if (isIOS && isSafari) {
        return (
            <>
                <br></br>
                <b>
                    {"Se hai attivato l'impostazione della privacy non potrai accedere con Google. "}
                    <Link
                        target="_blank"
                        href="https://assistenza.zanichelli.it/support/solutions/articles/4000178189-non-riesco-ad-autenticarmi-con-google-sul-browser-safari-del-mio-dispositivo-ios-cosa-posso-fare-"
                    >
                        {'Leggi come risolvere il problema'}
                    </Link>
                    {'.'}
                </b>
            </>
        );
    }

    if (isIOS && isChrome) {
        return (
            <>
                <br></br>
                <b>
                    {"Se hai attivato l'impostazione della privacy non potrai accedere con Google. "}
                    <Link
                        target="_blank"
                        href="https://assistenza.zanichelli.it/support/solutions/articles/4000178190-non-riesco-ad-autenticarmi-con-google-sul-browser-chrome-del-mio-dispositivo-ios-cosa-posso-fare-"
                    >
                        {'Leggi come risolvere il problema'}
                    </Link>
                    {'.'}
                </b>
            </>
        );
    }
};

export const AskForGoogleLogin = ({ reSignIn }) => {
    return (
        <FluidContainer paddingTop={80} paddingBottom={80}>
            <ColumnContainer width={[1, 2 / 3, 6 / 12]} paddingLeft={24} paddingRight={24}>
                <Heading level={2} weight="light">
                    {'Continua con Google'}
                </Heading>
                <FluidContainer marginTop={['tablet', 'desktop']} marginBottom={['tablet', 'desktop']} flexDirection="column">
                    <Body level={3}>
                        {'Clicca su Continua.'}
                        {
                            'Al tuo primo accesso dovrai inserire l’email che usi per Google Classroom e dare il consenso al trattamento dei dati necessari a ZTE per funzionare.'
                        }
                    </Body>
                    {isCrossSiteProblem && <CrossSiteProblemHelpMessage />}
                </FluidContainer>
                <Button onClick={() => reSignIn()}>{'CONTINUA'}</Button>
            </ColumnContainer>
        </FluidContainer>
    );
};

AskForGoogleLogin.propTypes = {
    reSignIn: PropTypes.func.isRequired,
};
export const MissingScopesMessage = ({ reSignIn }) => {
    const AlertMessage = styled.p`
        color: var(--red500);
    `;

    const ButtonsContainer = styled.div`
        display: flex;
        margin-top: 32px;

        & > button {
            margin-right: 16px;
        }

        @media (max-width: 767px) {
            flex-direction: column;

            & > button {
                width: 100%;
                margin-right: 0;
                margin-top: 16px;
            }

            & > a {
                width: 100%;
                margin-right: 0;
                margin-top: 16px;
            }
        }
    `;

    return (
        <FluidContainer paddingTop={80} paddingBottom={80} flexDirection="column">
            <ColumnContainer width={[1, 2 / 3, 6 / 12]} paddingLeft={24} paddingRight={24}>
                <Heading level={2} weight="light">
                    {'Continua con Google'}
                </Heading>

                <AlertMessage>
                    <b>{'Non hai ancora dato ai servizi ZTE di Zanichelli il consenso a collegarsi al tuo account Google.'}</b>
                </AlertMessage>
                <p>
                    {'Clicca su Continua.'}
                    <br />
                    {
                        'Al tuo primo accesso dovrai inserire l’email che usi per Google Classroom e dare il consenso al trattamento dei dati necessari a ZTE per funzionare.'
                    }
                </p>
                {isCrossSiteProblem && <CrossSiteProblemHelpMessage />}
                <ButtonsContainer>
                    <Button onClick={() => reSignIn()}>{'CONTINUA'}</Button>
                    <Button
                        type="tertiary"
                        as="a"
                        disabled={false}
                        href={DOMPurify.sanitize(getRedirectToSource(SOURCES.GOOGLE_CLASSROOM))}
                    >
                        {'ANNULLA E TORNA SU CLASSROOM'}
                    </Button>
                </ButtonsContainer>
            </ColumnContainer>
        </FluidContainer>
    );
};

MissingScopesMessage.propTypes = {
    reSignIn: PropTypes.func.isRequired,
};

const ClassWorkLoginComponent = ({
    assignment,
    assignmentId,
    googleUserInfo,
    googleUserStatus,
    getAssignmentById,
    getGoogleAssignmentEndDate,
    requestAccessToken,
    resumeGoogleLogin,
    setGoogleCourseWorkId,
}) => {
    const [askForLogIn, setAskForLogin] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [wrongClass, setWrongClass] = useState(false);
    const [gApiStatus] = useGapi();
    const [gCourseInfoStatus, gCourseInfo] = useGoogleCourseInfo(assignment ? assignment.classId : null, googleUserInfo);

    const history = useNavigate();

    useEffect(() => {
        setGoogleCourseWorkId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (gApiStatus.success) {
            if (!googleUserInfo) {
                resumeGoogleLogin();
            } else if (!googleUserStatus.gotAccessToken) {
                setAskForLogin(true);
            }
        }
    }, [googleUserInfo, gApiStatus.success, googleUserStatus.gotAccessToken, setAskForLogin, resumeGoogleLogin]);

    useEffect(() => {
        if (googleUserStatus.error) {
            setTimeout(history, 0, ERROR, { replace: true });
        }

        if (googleUserStatus.success) {
            getAssignmentById(assignmentId);
        }
    }, [googleUserStatus, getAssignmentById, assignmentId, history]);

    useEffect(() => {
        if (googleUserStatus.pending) {
            setAskForLogin(false);
            setWrongClass(false);
        }
    }, [googleUserStatus.pending]);

    useEffect(() => {
        const initAuthCheck = async () => {
            const gCourseInfoReady = gCourseInfoStatus.success && gCourseInfo;
            const gUserInfoReady = googleUserStatus.success;

            if (assignment.httpStatus.error) {
                setTimeout(history, 0, ERROR, { replace: true });
            }

            if (!assignment.error && assignment.classId && gCourseInfoReady && gUserInfoReady) {
                const { id: loggedUserId } = googleUserInfo;
                const { teachers, students } = gCourseInfo;
                let isTerminationTracked = false;

                const payload = generateClassroomViewerPayload(assignment, loggedUserId, teachers, students, isTerminationTracked);
                if (!payload) {
                    setWrongClass(true);
                    return;
                } else {
                    try {
                        const privacyConsentByUserId = await getPrivacyConsentByUserId();

                        if (privacyConsentByUserId) {
                            setShowPrivacy(false);
                            redirectToViewer(payload);
                        } else {
                            setShowPrivacy(true);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }

                if (loggedUserId && !showPrivacy && payload) {
                    try {
                        const mostRecentStatus = await getStatusByAssigmentIdAndStudentIdIn(assignment.id, loggedUserId);
                        const googleAssignmentEndDate = await getGoogleAssignmentEndDate(assignment.classId, assignment.googleCourseWorkId);

                        const assignmentEndDate = Date.parse(googleAssignmentEndDate);
                        const now = Date.now();

                        if (mostRecentStatus && mostRecentStatus?.length === 0 && assignmentEndDate < now) {
                            isTerminationTracked = true;
                        }

                        if (mostRecentStatus?.finished) {
                            isTerminationTracked = true;
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }
        };
        initAuthCheck();
    }, [
        assignment,
        googleUserInfo,
        googleUserStatus,
        gCourseInfo,
        gCourseInfoStatus.success,
        getGoogleAssignmentEndDate,
        history,
        showPrivacy,
    ]);

    if (wrongClass && googleUserInfo?.grantedScopes) {
        return <WrongClassroom onChangeUser={() => resumeGoogleLogin(true)}></WrongClassroom>;
    }

    if (askForLogIn) {
        return (
            <DefaultLayout exercises={[]} status={{}} profile={{ status: {} }}>
                <ModalContainer />
                <AskForGoogleLogin reSignIn={requestAccessToken} />
            </DefaultLayout>
        );
    }

    if (googleUserStatus.success || googleUserStatus.error) {
        if (googleUserInfo?.grantedScopes) {
            if (showPrivacy) {
                return (
                    <DefaultLayout exercises={[]} status={{}} profile={{ status: {} }}>
                        <ModalContainer />
                        <ClassworkPrivacyConsent
                            setPrivacyConsent={async () => {
                                if (await setPrivacyConsentByUserId()) {
                                    setShowPrivacy(false);
                                }
                            }}
                        />
                    </DefaultLayout>
                );
            } else {
                return <></>;
            }
        } else {
            return (
                <DefaultLayout exercises={[]} status={{}} profile={{ status: {} }}>
                    <ModalContainer />
                    <MissingScopesMessage reSignIn={requestAccessToken}></MissingScopesMessage>
                </DefaultLayout>
            );
        }
    } else {
        return <></>;
    }
};

ClassWorkLoginComponent.propTypes = {
    assignment: PropTypes.object,
    assignmentId: PropTypes.string,
    googleUserInfo: PropTypes.object,
    googleUserStatus: PropTypes.shape({
        error: PropTypes.bool,
        pending: PropTypes.bool,
        success: PropTypes.bool,
        gotAccessToken: PropTypes.bool,
    }),
    getAssignmentById: PropTypes.func,
    getGoogleAssignmentEndDate: PropTypes.func,
    requestAccessToken: PropTypes.func,
    resumeGoogleLogin: PropTypes.func,
    setGoogleCourseWorkId: PropTypes.func,
};

export default ClassWorkLoginComponent;
